import { Controller } from "@hotwired/stimulus"

// Drives a quick-create <dialog> (customer or site) opened from another form.
// Composed with timesheet-dialog (ESC/backdrop/bfcache handling) on the same
// element. Listens for the window event "quick-create:open" ({ dialogId, term,
// customerId }), submits the form via fetch as JSON, renders validation errors
// inline, and on success announces the record via "quick-create:created"
// ({ kind, id, name, customerId }) so the opener can inject + select it.
export default class extends Controller {
  static values = { kind: String, genericError: String }
  static targets = ["form", "nameInput", "baseError", "error", "submitButton", "customerSelect"]

  open(event) {
    if (event.detail.dialogId !== this.element.id) return

    this.formTarget.reset()
    this.clearErrors()
    this.nameInputTarget.value = event.detail.term || ""
    if (this.kindValue === "site") this.preselectCustomer(event.detail.customerId)

    this.element.showModal()
    document.body.style.overflow = "hidden"
  }

  async submit(event) {
    event.preventDefault()
    this.clearErrors()
    this.submitButtonTarget.disabled = true

    try {
      const response = await fetch(this.formTarget.action, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content,
        },
        body: new FormData(this.formTarget),
      })

      if (response.ok) {
        const data = await response.json()
        // Native close() hands focus back to the element that opened the dialog.
        this.element.close()
        window.dispatchEvent(
          new CustomEvent("quick-create:created", {
            detail: {
              kind: this.kindValue,
              id: String(data.id),
              name: data.name,
              customerId: data.customer_id == null ? "" : String(data.customer_id),
            },
          }),
        )
      } else if (response.status === 422) {
        const data = await response.json()
        this.renderErrors(data.errors || {})
      } else {
        const data = await response.json().catch(() => ({}))
        this.showBaseError([data.error || this.genericErrorValue])
      }
    } catch {
      this.showBaseError([this.genericErrorValue])
    } finally {
      this.submitButtonTarget.disabled = false
    }
  }

  // The dialog's customer select mirrors the opener's current customer. Goes
  // through the SlimSelect wrapper's setSelected (a bare value + change can be
  // reverted by SlimSelect on its first programmatic change after init).
  preselectCustomer(customerId) {
    if (!this.hasCustomerSelectTarget) return

    const select = this.customerSelectTarget
    const selectController = this.selectControllerFor(select)
    if (selectController) {
      selectController.setSelected(customerId || "")
    } else {
      select.value = customerId || ""
      select.dispatchEvent(new Event("change"))
    }
  }

  // The site dialog's customer options are rendered at page load, so a customer
  // quick-created afterwards would be missing here and its preselect would
  // silently fail — register it when the customer dialog announces it.
  registerCustomer(event) {
    if (this.kindValue !== "site" || event.detail.kind !== "customer") return
    if (!this.hasCustomerSelectTarget) return

    const select = this.customerSelectTarget
    if ([...select.options].some((option) => option.value === event.detail.id)) return

    select.append(new Option(event.detail.name, event.detail.id))
    // SlimSelect snapshots its data; rebuild it rather than relying on its
    // MutationObserver, which is deliberately deaf while SlimSelect updates itself.
    this.selectControllerFor(select)?.refresh()
  }

  selectControllerFor(select) {
    return this.application.getControllerForElementAndIdentifier(select, "select")
  }

  // Errors arrive as { attribute: [messages] } (nested attributes use dotted
  // keys like "contact_persons.firstname"). Messages land under the matching
  // [data-field] slot; everything without a slot goes into the base error box.
  renderErrors(errors) {
    const unmatched = []
    for (const [field, messages] of Object.entries(errors)) {
      const slot = this.errorTargets.find((el) => el.dataset.field === field)
      if (slot) {
        slot.textContent = messages.join(", ")
        slot.classList.remove("hidden")
      } else {
        unmatched.push(...messages)
      }
    }
    if (unmatched.length > 0) this.showBaseError(unmatched)
  }

  showBaseError(messages) {
    this.baseErrorTarget.textContent = messages.filter(Boolean).join(", ")
    this.baseErrorTarget.classList.remove("hidden")
  }

  clearErrors() {
    this.baseErrorTarget.textContent = ""
    this.baseErrorTarget.classList.add("hidden")
    this.errorTargets.forEach((el) => {
      el.textContent = ""
      el.classList.add("hidden")
    })
  }
}
